import { Link } from '@tanstack/react-router';
import { UserCheck } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Button, Card, Field, Input } from '../../../components/ui';
import { useT } from '../../../i18n';
import { useCheckAccount, type AccountCheckInput } from './api';
import { CheckResult } from './CheckResult';
import { normalizePhone, onlyDigits } from './format';
import { Fail } from './shared';

/**
 * Check an account by phone, IIN and BIN: TestRun logs into it on the stand
 * (OTP from Kafdrop, biometry in test mode) and a successful one joins the
 * pool by itself. QA and Admin only — the page does not show it to a Viewer.
 */
export const CheckTab = () => {
  const { t } = useT();
  const [form, setForm] = useState({ phone: '', iin: '', bin: '', fresh: false });
  // the credentials of the result on screen — its actions (payments) go with them
  const [checked, setChecked] = useState<AccountCheckInput | null>(null);
  const check = useCheckAccount();

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const phone = normalizePhone(form.phone);
    setForm((f) => ({ ...f, phone }));
    const bin = onlyDigits(form.bin);
    const input: AccountCheckInput = {
      phone,
      iin: onlyDigits(form.iin),
      ...(bin ? { bin } : {}),
      fresh: form.fresh,
    };
    setChecked({ ...input, fresh: false });
    check.mutate(input);
  };

  return (
    <div className="space-y-4">
      <Card>
        <form onSubmit={submit} className="space-y-4 px-4 py-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <Field
              label={t('automation.accounts.phone')}
              hint={t('automation.accounts.check.phoneHint')}
              required
            >
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  placeholder={t('automation.accounts.phonePlaceholder')}
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  onBlur={() => setForm((f) => ({ ...f, phone: normalizePhone(f.phone) }))}
                  autoComplete="off"
                  inputMode="tel"
                  required
                />
              )}
            </Field>
            <Field label={t('automation.accounts.iin')} required>
              {({ id }) => (
                <Input
                  id={id}
                  className="font-mono"
                  placeholder={t('automation.accounts.digits12')}
                  value={form.iin}
                  onChange={(e) => setForm((f) => ({ ...f, iin: e.target.value }))}
                  inputMode="numeric"
                  autoComplete="off"
                  required
                />
              )}
            </Field>
            <Field label={t('automation.accounts.bin')}>
              {({ id }) => (
                <Input
                  id={id}
                  className="font-mono"
                  placeholder={t('automation.accounts.check.binPlaceholder')}
                  value={form.bin}
                  onChange={(e) => setForm((f) => ({ ...f, bin: e.target.value }))}
                  inputMode="numeric"
                  autoComplete="off"
                />
              )}
            </Field>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <label className="flex cursor-pointer items-center gap-2 text-[13px] text-ink-muted">
              <input
                type="checkbox"
                checked={form.fresh}
                onChange={(e) => setForm((f) => ({ ...f, fresh: e.target.checked }))}
              />
              {t('automation.accounts.check.fresh')}
            </label>
            <Button type="submit" variant="primary" loading={check.isPending}>
              {check.isPending ? null : <UserCheck className="size-4" aria-hidden="true" />}
              {check.isPending
                ? t('automation.accounts.check.loggingIn')
                : t('automation.accounts.check.submit')}
            </Button>
          </div>
          {check.isPending ? (
            <p className="text-[12px] text-ink-subtle">{t('automation.accounts.check.slowHint')}</p>
          ) : null}
        </form>
      </Card>

      {check.isError ? (
        <Fail title={t('automation.accounts.check.failed')} error={check.error} />
      ) : null}

      {check.data?.poolId ? (
        <p className="text-[13px] text-ink-muted">
          {t('automation.accounts.check.inPool')}{' '}
          <Link
            to="/automation/accounts/$accountId"
            params={{ accountId: check.data.poolId }}
            className="text-accent-fg hover:underline"
          >
            {t('automation.accounts.check.openCard')}
          </Link>
        </p>
      ) : null}
      {check.data && checked ? (
        <CheckResult
          data={check.data}
          creds={{
            phone: checked.phone,
            iin: checked.iin,
            ...(checked.bin ? { bin: checked.bin } : {}),
          }}
          canAct
          onRefresh={() => check.mutate(checked)}
        />
      ) : null}
    </div>
  );
};
